
import { useApiStore } from "@/stores/apiStore";
import { useAuthStore } from "@/stores/authStore";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { useImportStore } from "@/stores/importStore";
import { useSiteStore } from "@/stores/siteStore";
import { useToastStore } from "@/stores/toastStore";
import { BLASTDOG_DATASET_NAME } from "@/utils/DisplayPaneConstants";
import ImportFile from "@/utils/UploadHelper";
import { FormatBytes, hasMutipleSpaces } from "@/utils/Utilities";
import { AxiosError } from "axios";
import moment from "moment-timezone";
import { defineComponent, PropType } from "vue"; // # todo - convert to Transition? Or is it correct as is?
import BlastDogImport from "./blastdog.vue";
import DatasetMapping from "./datasetMapping.vue";
import PreviouslyUploadedDialog from "./previouslyUploadedDialog.vue";
import UploadFiles from "./uploadFiles.vue";
class FileOptions {
	files = [];
	addMenu = false;
	constructor(files = [], addMenu = false) {
		this.files = files;
		this.addMenu = addMenu;
	}
}
interface FileOption {
	files: File[];
	addMenu: boolean;
}

interface KeysAsStrings {
	[key: string]: any;
}
export default defineComponent({
	name: "imdex-import",
	components: {
		PreviouslyUploadedDialog,
		UploadFiles,
		DatasetMapping,
		BlastDogImport
	},
	setup() {
		const api = useApiStore();
		const siteStore = useSiteStore();
		const authStore = useAuthStore();
		const formatBytes = FormatBytes;
		const toast = useToastStore();
		const hierarchyStore = useHierarchyStore();
		const importStore = useImportStore();
		return {
			hierarchyStore,
			importStore,
			siteStore,
			toast,
			authStore,
			api,
			moment,
			formatBytes
		};
	},
	data() {
		return {
			//COLLAR FILE OPTIONS
			collarOptions: new FileOptions() as FileOption,
			//SURVEY FILE OPTIONS
			surveyOptions: new FileOptions() as FileOption,
			//PLANNED COLLARS FILE OPTIONS
			plannedOptions: new FileOptions() as FileOption,
			//TRAJECTORY FILE OPTIONS
			trajectoryOptions: new FileOptions() as FileOption,
			//INTERVAL FILE OPTIONS
			intervalOptions: new FileOptions() as FileOption,
			//POINT FILE OPTIONS
			pointOptions: new FileOptions(),
			//BLOCKMODEL FILE OPTIONS
			blockmodelOptions: new FileOptions() as FileOption,
			//SHAPE FILE OPTIONS
			shapeOptions: new FileOptions() as FileOption,
			//POINT DATA FILE OPTIONS
			pointdataOptions: new FileOptions() as FileOption,
			selectAllPreviouslyUploadedFiles: false,
			previouslyUploadedDatasets: [],
			previouslyUploadedDialog: false,
			includePlanned: false,
			step: 0,
			tempGroup: "",
			dataset_name: "",
			uploadingFiles: false,
			filesToUpload: [],
			loadingInterval: null,
			uploadingDone: false,
			hierarchyOpen: false,
			activeTypeForPreviouslyUploadedDialog: "",
			finishedDatasetMaps: [],
			includeFolderName: false,
			folderName: "",
			selectMultiple: false,
			importAsMultiOrSingle: "single",
			errors: {}
		} as KeysAsStrings;
	},
	mounted() {
		this.tempGroup = this.group;
		if (this.folderPath != null && this.folderPath != "") {
			this.folderName = this.folderPath;
			this.includeFolderName = true;
		} else {
			this.folderName = "";
			this.includeFolderName = false;
		}
	},
	beforeUnmount() {
		this.collarOptions = new FileOptions();
		this.surveyOptions = new FileOptions();
		this.plannedOptions = new FileOptions();
		this.trajectoryOptions = new FileOptions();
		this.intervalOptions = new FileOptions();
		this.pointOptions = new FileOptions();
		this.blockmodelOptions = new FileOptions();
		this.shapeOptions = new FileOptions();
		this.pointdataOptions = new FileOptions();
		this.filesToUpload = [];
		this.uploadingFiles = false;
	},
	methods: {
		// Utility function for file type validation
		isValidFileType(file: any, validTypes: string[]): boolean {
			const extension = file.file.name.split(".").pop().toLowerCase();
			return validTypes.includes(extension);
		},
		// Validate all expected types are present in the files array
		processFiles(fileOptions: any, validTypes: any) {
			if (!fileOptions) return [];
			return fileOptions.filter((file: any) => {
				if (!this.isValidFileType(file, validTypes)) {
					this.toast.toast({
						type: "error",
						title: this.$t("import.uploadParent.toastErrorTitle"),
						message: this.$t("import.uploadParent.toastErrorMsgFileType"),
						dismissable: false
					});
					return false;
				}
				return true;
			});
		},
		async createDataset(endpoint: string, postData: any) {
			try {
				await this.api.post({ endpoint, post: postData });
				this.toast.toast({
					type: "success",
					title: this.$t("import.uploadParent.toastSuccessTitle"),
					message: "",
					dismissable: false
				});
				this.$emit("close");
				this.hierarchyStore.fetchHierarchy();
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						type: "error",
						title: e.response?.data.message,
						message: "",
						dismissable: false
					});
				}
			}
		},
		finishUploadingFiles(maps: any) {
			this.uploadingDone = true;
			this.finishedDatasetMaps = maps;
		},
		beginMapDataset() {
			this.step = 1;
		},
		cancelUpload() {
			if (this.$refs.uploadFiles != null) {
				(this.$refs.uploadFiles as any).cancelAll();
			}
		},
		showPreviouslyUploadedDialog(type: string) {
			this.activeTypeForPreviouslyUploadedDialog = type;
			this.previouslyUploadedDialog = true;
		},
		selectPreviousFiles(v: any) {
			v = v.map((item: any) => {
				return { ...item };
			});
			if (
				this.activeTypeForPreviouslyUploadedDialog == "interval" ||
				this.activeTypeForPreviouslyUploadedDialog == "point"
			) {
				this[`${this.activeTypeForPreviouslyUploadedDialog}Options`].files =
					this[`${this.activeTypeForPreviouslyUploadedDialog}Options`].files.concat(v);
			} else {
				this[`${this.activeTypeForPreviouslyUploadedDialog}Options`].files = this[
					`${this.activeTypeForPreviouslyUploadedDialog}Options`
				].files = v;
			}
			this.previouslyUploadedDialog = false;

			this.updateDatasetNameFromFiles(v);
		},
		beginUploadFiles() {
			this.errors = {};
			const nameCharacterLimit = 50;
			let filesToUpload = [];

			// Error checking
			if (this.dataset_name.length > nameCharacterLimit) {
				this.errors.dataset_name = true;
			}

			if (this.folderName.length > nameCharacterLimit) {
				this.errors.folderName = true;
			}

			if (hasMutipleSpaces(this.dataset_name) || hasMutipleSpaces(this.folderName)) {
				this.errors.hasMultipleSpaces = true;
			}

			if (this.errors.folderName || this.errors.dataset_name) {
				this.toast.toast({
					type: "error",
					title: this.$t("import.uploadParent.toastErrorTitle"),
					message: this.$t("import.uploadParent.toastErrorMsgLength"),
					dismissable: false
				});
				return;
			}

			if (this.errors.hasMultipleSpaces) {
				this.toast.toast({
					type: "error",
					title: this.$t("import.uploadParent.toastErrorTitle"),
					message: this.$t("import.uploadParent.toastErrorMsgSubstr"),
					dismissable: false
				});
				return;
			}

			// Process file based on type
			switch (this.type) {
				case "drillhole":
					filesToUpload = this.processFiles(this.processDrillholesFiles(), [
						"csv",
						"zip"
					]);
					break;
				case "blockmodel":
					filesToUpload = this.processFiles(this.processBlockModel(), [
						"dat",
						"dm",
						"zip",
						"csv"
					]);
					break;
				case "shape":
					filesToUpload = this.processFiles(this.processShape(), ["dxf", "zip"]);
					break;
				case "point":
					filesToUpload = this.processFiles(this.processPoint(), ["csv", "zip", "gas"]);
					break;
				default:
					break;
			}

			// Update filesToUpload with the processed files and set progress
			this.filesToUpload = filesToUpload.map((item: any) => ({ ...item, progress: 0 }));

			if (this.filesToUpload.length > 0) {
				this.uploadingFiles = true;
			} else {
				this.toast.toast({
					type: "error",
					title: this.$t("import.uploadParent.toastErrorTitle"),
					message: this.$t("import.uploadParent.toastErrorMsgNoFiles"),
					dismissable: false
				});
			}
		},
		processDrillholesFiles() {
			let files: any[] = [];
			const availableUploadOptionFiles = [
				this.collarOptions.files,
				this.surveyOptions.files,
				this.plannedOptions.files,
				this.trajectoryOptions.files,
				this.intervalOptions.files,
				this.pointOptions.files
			];
			if (this.folderName && !this.includeFolderName) {
				this.folderName = "";
			}
			let isInvalidFileDetected = false;
			const fileNames = [] as string[];
			availableUploadOptionFiles.forEach((optionFiles: any) => {
				optionFiles.forEach((file: any, index: number) => {
					const strCheck = file.name.split(".")[file.name.split(".").length - 1];
					if (strCheck != "csv" && strCheck != "zip") {
						optionFiles[index] = new ImportFile(
							{ file: file, status: "none", progress: 0 },
							this.dataset_name,
							this.group,
							this.folderName,
							this.api as any,
							(item: string) => {
								return item;
							},
							(this.siteStore as any).activeSite.id
						);
						optionFiles[index].name = optionFiles[index].file.name;
						optionFiles[index].size = optionFiles[index].file.size;
						optionFiles[index].hasError = true;
						optionFiles[index].status = this.$t("import.uploadParent.dhErrorFileType");
						isInvalidFileDetected = true;
					}
					if (optionFiles[index].hasError) isInvalidFileDetected = true;
					if (fileNames.includes(file.name)) {
						optionFiles[index].hasError = true;
						optionFiles[index].status = this.$t("import.uploadParent.dhErrorNameUsed");
						isInvalidFileDetected = true;
					} else {
						fileNames.push(file.name);
					}
				});
			});
			if (isInvalidFileDetected) {
				return;
			}
			files = files.concat(
				Array.from(this.collarOptions.files).map((item: any) => {
					return { file: item, type: "collar" };
				})
			);
			files = files.concat(
				Array.from(this.surveyOptions.files).map((item: any) => {
					return { file: item, type: "survey" };
				})
			);
			files = files.concat(
				Array.from(this.plannedOptions.files).map((item: any) => {
					return { file: item, type: "planned" };
				})
			);
			files = files.concat(
				Array.from(this.trajectoryOptions.files).map((item: any) => {
					return { file: item, type: "trajectory" };
				})
			);
			files = files.concat(
				Array.from(this.intervalOptions.files).map((item: any) => {
					return { file: item, type: "interval" };
				})
			);
			files = files.concat(
				Array.from(this.pointOptions.files).map((item: any) => {
					return { file: item, type: "point" };
				})
			);
			files = files.map((item) => {
				return new ImportFile(
					item,
					this.dataset_name,
					this.group,
					this.folderName,
					this.api as any,
					(item: string) => {
						return item;
					},
					(this.siteStore as any).activeSite.id
				);
			});

			return files;
		},
		processBlockModel() {
			let files: any[] = [];

			if (this.folderName && !this.includeFolderName) {
				this.folderName = "";
			}

			for (let i = 0; i < this.blockmodelOptions.files.length; i++) {
				const strCheck =
					this.blockmodelOptions.files[i].name.split(".")[
						this.blockmodelOptions.files[i].name.split(".").length - 1
					];
				if (
					strCheck != "dat" &&
					strCheck != "dm" &&
					strCheck != "zip" &&
					strCheck != "csv"
				) {
					this.blockmodelOptions.files[i] = new ImportFile(
						{
							file: this.blockmodelOptions.files[i],
							status: "none",
							progress: 0
						},
						this.dataset_name,
						this.group,
						this.folderName,
						this.api as any,
						(item: string) => {
							return item;
						},
						(this.siteStore as any).activeSite.id
					);
					this.blockmodelOptions.files[i].name =
						this.blockmodelOptions.files[i].file.name;
					this.blockmodelOptions.files[i].size =
						this.blockmodelOptions.files[i].file.size;
					this.blockmodelOptions.files[i].hasError = true;
					this.blockmodelOptions.files[i].status = this.$t(
						"import.uploadParent.bmErrorFileType"
					);
					return;
				}
			}
			files = files.concat(
				Array.from(this.blockmodelOptions.files).map((item: any) => {
					return { file: item, type: "blockmodel" };
				})
			);

			this.filesToUpload = files;

			this.filesToUpload = this.filesToUpload.map((item: any) => {
				return { ...item, progress: 0 };
			});

			return files;
		},
		processShape() {
			let files: any[] = [];

			if (this.folderName && !this.includeFolderName) {
				this.folderName = "";
			}

			for (let i = 0; i < this.shapeOptions.files.length; i++) {
				const strCheck =
					this.shapeOptions.files[i].name.split(".")[
						this.shapeOptions.files[i].name.split(".").length - 1
					];
				if (strCheck != "dxf" && strCheck != "zip") {
					this.shapeOptions.files[i] = new ImportFile(
						{ file: this.shapeOptions.files[i], status: "none", progress: 0 },
						this.dataset_name,
						this.group,
						this.folderName,
						this.api as any,
						(item: string) => {
							return item;
						},
						(this.siteStore as any).activeSite.id
					);
					this.shapeOptions.files[i].name = this.shapeOptions.files[i].file.name;
					this.shapeOptions.files[i].size = this.shapeOptions.files[i].file.size;
					this.shapeOptions.files[i].hasError = true;
					this.shapeOptions.files[i].status = this.$t(
						"import.uploadParent.shapeErrorFileType"
					);

					return;
				}
			}
			files = files.concat(
				Array.from(this.shapeOptions.files).map((item: any) => {
					return { file: item, type: "shape" };
				})
			);

			this.filesToUpload = files;

			this.filesToUpload = this.filesToUpload.map((item: any) => {
				return { ...item, progress: 0 };
			});

			return files;
		},
		processPoint() {
			let files: any[] = [];

			if (this.folderName && !this.includeFolderName) {
				this.folderName = "";
			}

			for (let i = 0; i < this.pointdataOptions.files.length; i++) {
				const strCheck =
					this.pointdataOptions.files[i].name.split(".")[
						this.pointdataOptions.files[i].name.split(".").length - 1
					];
				if (strCheck != "csv" && strCheck != "zip" && strCheck != "gas") {
					this.pointdataOptions.files[i] = new ImportFile(
						{
							file: this.pointdataOptions.files[i],
							status: "none",
							progress: 0
						},
						this.dataset_name,
						this.group,
						this.folderName,
						this.api as any,
						(item: string) => {
							return item;
						},
						(this.siteStore as any).activeSite.id
					);
					this.pointdataOptions.files[i].name = this.pointdataOptions.files[i].file.name;
					this.pointdataOptions.files[i].size = this.pointdataOptions.files[i].file.size;
					this.pointdataOptions.files[i].hasError = true;
					this.pointdataOptions.files[i].status = this.$t(
						"import.uploadParent.pointErrorFileType"
					);

					return;
				}
			}
			files = files.concat(
				Array.from(this.pointdataOptions.files).map((item: any) => {
					return { file: item, type: "pointdata" };
				})
			);

			this.filesToUpload = files;

			this.filesToUpload = this.filesToUpload.map((item: any) => {
				return { ...item, progress: 0 };
			});

			return files;
		},
		browse(fileType: string): any {
			const options: any = this[`${fileType}Options`];
			options.addMenu = false;
			const fileInput: any = this.$refs[`${fileType}FileInput`];
			fileInput.click();
		},
		selectedFiles(e: any, type: string): any {
			if (!e || !e.target || !e.target.files) return;
			const options = this[`${type}Options`];
			if (type == "interval" || type == "point") {
				options.files = options.files.concat(Array.from(e.target.files));
			} else {
				options.files = [Array.from(e.target.files)[0]];
				this.updateDatasetNameFromFiles(options.files as File[]);
			}

			e.target.value = null;
		},
		updateDatasetNameFromFiles(files: File[]) {
			if (!this.dataset_name && files.length > 0) {
				const f: File = files[0] as File;
				const fileNameWithoutExtension = f.name.replace(/\.[^/.]+$/, "");
				this.dataset_name = fileNameWithoutExtension;
			}
		},
		removeFile(filename: string, type: string): any {
			const options = this[`${type}Options`];
			const fileListCopy = [...Array.from(options.files)];
			fileListCopy.splice(fileListCopy.map((item: any) => item.name).indexOf(filename), 1);
			options.files = fileListCopy;
		}
	},
	computed: {
		groups() {
			return this.hierarchyStore.hierarchy
				.filter((item) => {
					return item.name.toLowerCase() != BLASTDOG_DATASET_NAME.toLowerCase();
				})
				.map((item: { name: string; levelType: string }) => {
					return item.name;
				});
		},
		meta() {
			return {
				name: this.dataset_name,
				folderPath: this.folderName,
				groupName: this.group
			};
		},
		filesReady(): number {
			const typeOptionsMap = {
				drillhole: this.collarOptions,
				shape: this.shapeOptions,
				blockmodel: this.blockmodelOptions,
				point: this.pointdataOptions
			};

			const options = typeOptionsMap[this.type as keyof typeof typeOptionsMap];

			if (!options) return 0;

			if (
				this.type === "drillhole" &&
				(this.intervalOptions.files.length > 0 || this.pointOptions.files.length > 0)
			) {
				return options.files.length + this.surveyOptions.files.length;
			}
			return options.files.length;
		}
	},
	props: {
		type: {
			type: String,
			default: "drillhole"
		},
		folderPath: {
			type: String,
			default: ""
		},
		group: {
			type: String,
			default: "Production"
		},
		subFolderOptions: {
			type: Array as PropType<string[]>,
			default: () => []
		}
	},
	watch: {
		uploadingDone(v) {
			// watching for when shape uploading complete to just fire the create dataset endpoint from here and close.
			if (v) {
				this.$nextTick(() => {
					if (this.type == "shape") {
						this.createDataset("/dataset-surface/create", {
							id:
								this.finishedDatasetMaps[0].fileId == ""
									? this.finishedDatasetMaps[0].file.fileId
									: this.finishedDatasetMaps[0].fileId,
							name: this.dataset_name,
							groupName: this.tempGroup,
							folderPath: this.folderName
						});
					}
				});
			}
		},
		previouslyUploadedDialog() {
			this.collarOptions.addMenu = false;
			this.surveyOptions.addMenu = false;
			this.plannedOptions.addMenu = false;
			this.trajectoryOptions.addMenu = false;
			this.intervalOptions.addMenu = false;
			this.pointOptions.addMenu = false;
			this.blockmodelOptions.addMenu = false;
			this.shapeOptions.addMenu = false;
			this.pointdataOptions.addMenu = false;
		}
	}
});
